import React, { FunctionComponent, useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { useMedia } from 'react-media-match';
import { FormattedMessage } from 'react-intl';
import useConfig from '@empiriecom/module-components/hooks/useConfig';
import getIcon from '@empiriecom/module-components/utils/getIcon';
import { FragmentConfiguration } from '@/config';
import { MediaAsset } from '@/src/interfaces';
import StyledSlickProvider from './styledProvider';
import { useGalleryContext } from './GalleryContext';
import { Arrow } from './GalleryArrow';
import { GallerySlider } from './GallerySlider';
import { GalleryZoomOverlay } from './GalleryZoomOverlay';
import ThumbnailSlider from './ThumbnailSlider';
import { useProduct } from '../ProductContext';

const ImageGalleryWrapper = styled.div<{ isMobile: boolean }>`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-flow: row wrap;
  width: 95%;
  max-width: 660px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
  #share {
    position: absolute;
    top: 0.75rem;
    right: 0.75rem;
    z-index: 2;
  }
`;

const ImageGalleryProvider = styled(StyledSlickProvider)`
  width: 100%;
  position: relative;
  cursor: pointer;
  line-height: 0;
`;

const ZoomButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(255, 255, 255, 0.75);
  border-radius: 1.65rem;
  outline: none;
  border: 0px none;
  box-shadow: none;
  padding: 0.2rem 0.825rem;
  height: auto;
  z-index: 10;
  cursor: pointer;
  text-align: center;
  font-size: 0.75rem;
  position: absolute;
  bottom: 3%;
  left: 50%;
  transform: translate(-50%);
  user-select: none;
`;

const ZoomIcon = styled(getIcon('Fullscreen'))`
  width: 1.25rem;
  height: 1.25rem;
  margin-right: 0.3rem;
`;

const ScrollMenuWrapper = styled.div<{ isMobile: boolean; isTablet: boolean }>`
  width: 100%;
  position: relative;
  ${({ isMobile }) => !isMobile && '.menu-item-wrapper:first-of-type .thumbnail {margin-left: 0;}'}
  ${({ isMobile }) =>
    isMobile && 'background-color: rgb(244, 244, 244); padding: 0.75rem 0px 1.5rem;'}
  ${({ isMobile }) => !isMobile && 'margin-top: 1rem;'}
`;

export interface ImageGalleryProps {
  /** Array containing the product image source(s). */
  images: MediaAsset[];
  /** The product name. */
  name: string;
  /** The product sku. */
  sku?: string;
}

/**
 * <p>This is the image gallery. It is a composure of three subcomponents. The three components communicate
 * via the `GalleryContext`:</p>
 * <ul>
 *  <li>`GallerySlider`</li>
 *  <li>`ThumbnailSlider`</li>
 *  <li>`ZoomOverlay`</li>
 * </ul>
 * <p>If the optional `sku` parameter is provided the `ImageGallery` is sensible to this value. This results in
 * resetting the currently active image to the first image of the availble images if the value of the `sku`
 * parameter changes.</p>
 * <h3>GallerySlider</h3>
 * <p>This component renders the currently selected product image. The slide navigation (swipe to slide)
 * is enabled for mobile devices only.</p>
 * <h3>ThumbnailSlider</h3>
 * <p>This component renders the thumbnails for each available product image. It is located
 * below the `GallerySlider`. For mobile devices a swipe to navigate handler is enabled. On desktop devices
 * navigation arrows are provided to move the `ThumbnailSlider`.</p>
 * <p>The currently selected image is displayed with `opacity: 1;`. If a `Thumbnail` is clicked it causes an
 * update to each of the three sub components and the selected image will be displayed.</p>
 * <h3>ZoomOverlay</h3>
 * <p>The `ZoomOverlay` component can either be opened via clicking the product image or via clicking the
 * "fullscreen button" which is located in the bottom area of the product image.</p>
 * <p>If one of these two components gets clicked a fullscreen layer will be opened. This fullscreen layer provides
 * a slider navigation for all product images. If the currently selected product image is changed via the fullscreen
 * slider all other sub components will be updated accordingly.</p>
 * <p>If the product image is clicked it will trigger the zoom functionality. The product image gets zoomed in and
 * will be draggable to provide a more detailed view. If the image is clicked again the zoom will be reset to the initial
 * zoom and the draggability will be deactivated.</p>
 */
export const ImageGallery: FunctionComponent<ImageGalleryProps> = ({
  images,
  name,
  // sku = '',
}): JSX.Element => {
  const {
    setActiveImage,
    getActiveImage,
    setIsZoom,
    getIsZoom,
    getCurrentSlider,
  } = useGalleryContext();
  const {
    product: { sku },
  } = useProduct();
  const [isAnimating, setIsAnimating] = useState<boolean>(false);
  const device = useMedia({ desktop: 'desktop', mobile: 'mobile', tablet: 'desktop' });
  const isTablet = useMedia({ desktop: false, mobile: false, tablet: true });
  const config = useConfig<FragmentConfiguration>();
  const skuWithoutSize = sku.substring(0, sku.indexOf('-'));
  const isMobile = device === 'mobile';

  useEffect(() => {
    setActiveImage(0);
  }, [skuWithoutSize]);

  return (
    <div data-testid="image-gallery">
      <ImageGalleryWrapper isMobile={isMobile}>
        <ImageGalleryProvider data-testid="image-slider">
          <GallerySlider images={images} name={name} />
          {images[getActiveImage()]?.type !== 'CLIPLISTER' && (
            <ZoomButton data-testid="zoom-button" onClick={() => setIsZoom(true)}>
              <ZoomIcon className="zoom-icon" />
              <span>
                <FormattedMessage defaultMessage="Vollbild" id="image-gallery.fullscreen" />
              </span>
            </ZoomButton>
          )}
        </ImageGalleryProvider>
        <ScrollMenuWrapper isMobile={isMobile} isTablet={isTablet}>
          {!isMobile && (
            <Arrow
              isDisabled={getActiveImage() === 0}
              type="PREV"
              onClick={() => {
                if (!isAnimating) {
                  setIsAnimating(true);
                  setActiveImage(getActiveImage() - 1);
                  setTimeout(() => setIsAnimating(false), 350);
                }
              }}
            />
          )}
          <div style={{ overflow: 'hidden', width: '100%' }}>
            <ThumbnailSlider images={images} name={name} slider={getCurrentSlider()} />
          </div>
          {!isMobile && (
            <Arrow
              isDisabled={getActiveImage() === images.length - 1}
              type="NEXT"
              onClick={() => {
                if (!isAnimating) {
                  setIsAnimating(true);
                  setActiveImage(getActiveImage() + 1);
                  setTimeout(() => setIsAnimating(false), 350);
                }
              }}
            />
          )}
        </ScrollMenuWrapper>
        {getIsZoom() &&
          ReactDOM.createPortal(
            <GalleryZoomOverlay images={images} config={config} device={device} />,
            typeof document !== 'undefined' && document.getElementsByTagName('body')[0],
          )}
      </ImageGalleryWrapper>
    </div>
  );
};

export default ImageGallery;
